import React, { useMemo } from 'react';
import {
    Checkbox,
    CheckboxVisibility,
    DefaultButton,
    DetailsListLayoutMode,
    DetailsRow,
    Dropdown,
    IColumn,
    IDetailsRowProps,
    IDropdownOption,
    ITheme,
    Icon,
    IconButton,
    Link,
    SelectionMode,
    ShimmeredDetailsList,
    Stack,
    Text,
    TooltipHost,
} from '@fluentui/react';
import {
    SreEntityType,
    SreInflightItem,
    SreInflightSearchResponse,
    SreSortDirection,
    SRE_PAGE_SIZE_OPTIONS,
} from '../../../../../AGSS/models/SreHealth';
import { SreProgressState } from './useSreProgress';
import { SreProgressDetail } from './SreProgressDetail';
import { CopyButton } from './CopyButton';
import { ageInHours, formatAge } from './formatting';

interface ISreTriageListProps {
    type: SreEntityType;
    result: SreInflightSearchResponse | null;
    isLoading: boolean;
    theme: ITheme;
    expanded: string[];
    selected: number[];
    progress: SreProgressState;
    sortBy: string;
    sortDirection: SreSortDirection;
    onToggleExpanded: (uniqueIdentifier: string) => void;
    onOpenDetails: (uniqueIdentifier: string) => void;
    onToggleSelected: (rowId: number) => void;
    onToggleSelectAll: (rowIds: number[], select: boolean) => void;
    onSort: (field: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
}

const AGE_WARNING_HOURS = 24;
const AGE_DANGER_HOURS = 144;
const RETRY_WARNING = 3;

export const SreTriageList: React.FC<ISreTriageListProps> = ({
    type,
    result,
    isLoading,
    theme,
    expanded,
    selected,
    progress,
    sortBy,
    sortDirection,
    onToggleExpanded,
    onOpenDetails,
    onToggleSelected,
    onToggleSelectAll,
    onSort,
    onPageChange,
    onPageSizeChange,
}) => {
    const items = result?.items ?? [];
    const page = result?.page;

    const pageIdentifiers = useMemo(
        () => items.map((item) => item.id),
        [items]
    );
    const selectedOnPage = useMemo(
        () => pageIdentifiers.filter((id) => selected.includes(id)).length,
        [pageIdentifiers, selected]
    );
    const allOnPageSelected =
        pageIdentifiers.length > 0 && selectedOnPage === pageIdentifiers.length;

    const columns: IColumn[] = useMemo(() => {
        const sortable = (key: string, field: string) => ({
            isSorted: sortBy === field,
            isSortedDescending: sortBy === field && sortDirection === 'desc',
            onColumnClick: () => onSort(field),
        });

        const built: IColumn[] = [
            {
                key: 'select',
                name: '',
                fieldName: 'select',
                minWidth: 24,
                maxWidth: 24,
                isIconOnly: true,
                onRenderHeader: () => (
                    <Checkbox
                        checked={allOnPageSelected}
                        indeterminate={selectedOnPage > 0 && !allOnPageSelected}
                        title="Select every row on this page"
                        ariaLabel="Select every row on this page"
                        onChange={() =>
                            onToggleSelectAll(
                                pageIdentifiers,
                                !allOnPageSelected
                            )
                        }
                    />
                ),
                onRender: (item: SreInflightItem) => (
                    <Checkbox
                        checked={selected.includes(item.id)}
                        title={`Select ${item.uniqueIdentifier}`}
                        ariaLabel={`Select ${item.uniqueIdentifier}`}
                        onChange={() => onToggleSelected(item.id)}
                    />
                ),
            },
            {
                key: 'expand',
                name: '',
                fieldName: 'expand',
                minWidth: 24,
                maxWidth: 24,
                isIconOnly: true,
                onRender: (item: SreInflightItem) => (
                    <IconButton
                        iconProps={{
                            iconName: expanded.includes(item.uniqueIdentifier)
                                ? 'ChevronDown'
                                : 'ChevronRight',
                        }}
                        title="Show stage progress"
                        ariaLabel="Show stage progress"
                        aria-expanded={expanded.includes(item.uniqueIdentifier)}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpanded(item.uniqueIdentifier);
                        }}
                        styles={{ root: { height: '28px' } }}
                    />
                ),
            },
            {
                key: 'uniqueIdentifier',
                name: 'Unique identifier',
                fieldName: 'uniqueIdentifier',
                minWidth: 260,
                maxWidth: 420,
                isMultiline: true,
                isResizable: true,
                ...sortable('uniqueIdentifier', 'uniqueIdentifier'),
                onRender: (item: SreInflightItem) => (
                    // The button is pinned right rather than trailing the text, so it lands in the
                    // same place on every row instead of moving with identifier length.
                    <Stack
                        horizontal
                        verticalAlign="center"
                        tokens={{ childrenGap: 4 }}
                        styles={{ root: { width: '100%' } }}
                    >
                        <Link
                            onClick={() => onOpenDetails(item.uniqueIdentifier)}
                            styles={{
                                root: {
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    wordBreak: 'break-all',
                                    whiteSpace: 'normal',
                                    overflowWrap: 'anywhere',
                                    textAlign: 'left',
                                    flexGrow: 1,
                                    minWidth: 0,
                                },
                            }}
                        >
                            {item.uniqueIdentifier}
                        </Link>
                        <CopyButton
                            value={item.uniqueIdentifier}
                            label="identifier"
                        />
                    </Stack>
                ),
            },
            {
                key: 'stage',
                name: 'Stage',
                fieldName: 'stage',
                minWidth: 190,
                maxWidth: 250,
                isResizable: true,
                ...sortable('stage', 'stage'),
                onRender: (item: SreInflightItem) => {
                    // Stage number is the enum value stored in the DB; order is the plan position.
                    // They disagree, so both are surfaced rather than picking one.
                    const label =
                        item.stageNumber >= 0
                            ? `${item.stageNumber} · ${item.stage}`
                            : item.stage;
                    const tooltip = item.stageOrder
                        ? `Step ${item.stageOrder} of ${item.stageCount} in ${item.orchestrationType}`
                        : 'Plan position unavailable';

                    return (
                        <TooltipHost content={tooltip}>
                            <Text variant="small">{label}</Text>
                        </TooltipHost>
                    );
                },
            },
            {
                key: 'stageState',
                name: 'State',
                fieldName: 'stageState',
                minWidth: 80,
                maxWidth: 110,
                ...sortable('stageState', 'stageState'),
                onRender: (item: SreInflightItem) => (
                    <Text variant="small">{item.stageState}</Text>
                ),
            },
            {
                key: 'age',
                name: 'Age',
                fieldName: 'age',
                minWidth: 70,
                maxWidth: 90,
                ...sortable(
                    'age',
                    type === 'build' ? 'stagedInLowshare' : 'writtenTime'
                ),
                onRender: (item: SreInflightItem) => {
                    const source = item.stagedInLowshare ?? item.writtenTime;
                    const hours = ageInHours(source);
                    const color =
                        hours >= AGE_DANGER_HOURS
                            ? theme.palette.redDark
                            : hours >= AGE_WARNING_HOURS
                            ? theme.palette.orange
                            : theme.palette.neutralPrimary;

                    return (
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    color,
                                    fontWeight:
                                        hours >= AGE_WARNING_HOURS ? 600 : 400,
                                },
                            }}
                        >
                            {formatAge(source)}
                        </Text>
                    );
                },
            },
            {
                key: 'retryCount',
                name: 'Retries',
                fieldName: 'retryCount',
                minWidth: 60,
                maxWidth: 80,
                ...sortable('retryCount', 'retryCount'),
                onRender: (item: SreInflightItem) => (
                    <Text
                        variant="small"
                        styles={{
                            root: {
                                color:
                                    item.retryCount > RETRY_WARNING
                                        ? theme.palette.redDark
                                        : theme.palette.neutralPrimary,
                                fontWeight:
                                    item.retryCount > RETRY_WARNING ? 600 : 400,
                            },
                        }}
                    >
                        {item.retryCount}
                    </Text>
                ),
            },
            {
                key: 'latestStatusMessage',
                name: 'Latest message',
                fieldName: 'latestStatusMessage',
                minWidth: 220,
                isResizable: true,
                isMultiline: false,
                onRender: (item: SreInflightItem) => (
                    <TooltipHost content={item.latestStatusMessage}>
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    color: theme.palette.neutralSecondary,
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                },
                            }}
                        >
                            {item.latestStatusMessage || '-'}
                        </Text>
                    </TooltipHost>
                ),
            },
        ];

        return built;
    }, [
        allOnPageSelected,
        expanded,
        onSort,
        onToggleExpanded,
        onOpenDetails,
        onToggleSelectAll,
        onToggleSelected,
        pageIdentifiers,
        selected,
        selectedOnPage,
        sortBy,
        sortDirection,
        theme,
        type,
    ]);

    // Derived from the columns rather than hard-coded, so adding one cannot silently reintroduce the
    // squeeze. The padding allowance covers Fluent's per-cell padding, which minWidth excludes.
    const minTableWidth = useMemo(
        () =>
            columns.reduce((sum, column) => sum + (column.minWidth ?? 0), 0) +
            columns.length * 16,
        [columns]
    );

    const onRenderRow = (props?: IDetailsRowProps): JSX.Element | null => {
        if (!props) {
            return null;
        }

        const item = props.item as SreInflightItem;
        const isExpanded = expanded.includes(item.uniqueIdentifier);

        return (
            <div key={item.id}>
                <DetailsRow {...props} />
                {isExpanded && (
                    <div
                        role="region"
                        aria-label={`Progress for ${item.uniqueIdentifier}`}
                        style={{
                            width: '100cqw',
                            maxWidth: '100%',
                            position: 'sticky',
                            left: 0,
                        }}
                    >
                        <SreProgressDetail
                            compact
                            item={item}
                            progress={progress.get(item.uniqueIdentifier)}
                            theme={theme}
                            onRefresh={() =>
                                progress.load(item.uniqueIdentifier, true)
                            }
                        />
                    </div>
                )}
            </div>
        );
    };

    const pageSizeOptions: IDropdownOption[] = SRE_PAGE_SIZE_OPTIONS.map(
        (size) => ({
            key: size,
            text: `${size} / page`,
        })
    );

    const pageIsOutOfRange = Boolean(
        page && page.pageNumber > Math.max(1, page.totalPages)
    );
    const showEmptyState =
        !isLoading &&
        items.length === 0 &&
        page?.totalCount === 0 &&
        !pageIsOutOfRange;

    return (
        <Stack tokens={{ childrenGap: 8 }}>
            {/*
                The columns need more width than a laptop has once the admin nav takes its ~300px,
                and justified layout squeezes them rather than overflowing, so the last column used
                to be cut off with nothing to scroll. The wrapper is the scroll container; the floor
                below stops the squeeze so the wrapper actually has something to scroll.
            */}
            <div style={{ overflowX: 'auto', containerType: 'inline-size' }}>
                <ShimmeredDetailsList
                    items={items}
                    columns={columns}
                    setKey={`sre-triage-${type}`}
                    layoutMode={DetailsListLayoutMode.justified}
                    selectionMode={SelectionMode.none}
                    checkboxVisibility={CheckboxVisibility.hidden}
                    enableShimmer={isLoading && items.length === 0}
                    shimmerLines={10}
                    onRenderRow={onRenderRow}
                    ariaLabelForShimmer="Loading in-flight orchestrations"
                    styles={{
                        root: { overflowX: 'visible', minWidth: minTableWidth },
                    }}
                />
            </div>

            {showEmptyState && (
                <Stack
                    horizontalAlign="center"
                    tokens={{ childrenGap: 6 }}
                    styles={{ root: { padding: '32px 0' } }}
                >
                    <Icon
                        iconName="SearchIssue"
                        styles={{
                            root: {
                                fontSize: 28,
                                color: theme.palette.neutralTertiary,
                            },
                        }}
                    />
                    <Text
                        variant="medium"
                        styles={{ root: { fontWeight: 600 } }}
                    >
                        Nothing in flight matches this filter
                    </Text>
                    <Text
                        variant="small"
                        styles={{
                            root: { color: theme.palette.neutralSecondary },
                        }}
                    >
                        Try clearing the identifier search or widening the age
                        filter.
                    </Text>
                </Stack>
            )}

            {page && page.totalCount > 0 && !pageIsOutOfRange && (
                <Stack
                    horizontal
                    wrap
                    verticalAlign="center"
                    tokens={{ childrenGap: 12 }}
                    styles={{ root: { paddingTop: '4px' } }}
                >
                    <DefaultButton
                        text="Previous"
                        iconProps={{ iconName: 'ChevronLeft' }}
                        disabled={page.pageNumber <= 1 || isLoading}
                        onClick={() => onPageChange(page.pageNumber - 1)}
                    />
                    <Text variant="small">
                        Page {page.pageNumber} of {Math.max(page.totalPages, 1)}
                    </Text>
                    <DefaultButton
                        text="Next"
                        iconProps={{ iconName: 'ChevronRight' }}
                        disabled={
                            page.pageNumber >= page.totalPages || isLoading
                        }
                        onClick={() => onPageChange(page.pageNumber + 1)}
                    />
                    <Dropdown
                        options={pageSizeOptions}
                        selectedKey={page.pageSize}
                        onChange={(_, option) =>
                            option && onPageSizeChange(option.key as number)
                        }
                        styles={{ root: { width: '120px' } }}
                    />
                    <Text
                        variant="small"
                        styles={{
                            root: { color: theme.palette.neutralSecondary },
                        }}
                    >
                        Showing{' '}
                        {items.length
                            ? (page.pageNumber - 1) * page.pageSize + 1
                            : 0}
                        –
                        {items.length
                            ? Math.min(
                                  (page.pageNumber - 1) * page.pageSize +
                                      items.length,
                                  page.totalCount
                              )
                            : 0}{' '}
                        of {page.totalCount.toLocaleString()}
                    </Text>
                </Stack>
            )}
        </Stack>
    );
};
